import React, { useState, useEffect, useRef } from "react";
import { Check, Loader, ChevronDown } from "lucide-react";
import { useAuth } from "../hooks/useAuth";
import API_CONFIG from "../lib/ApiConfig";
import BuyerInfoModal from "./BuyerInfoModal";
import analyticsService from "../lib/AnalyticsService";
import { useInteractionTracking } from "../hooks/useAnalytics";
import { useFlow } from "../hooks/useFlow";
import confetti from "canvas-confetti";
import ReinTcpaDisclosure from "./common/ReinTcpaDisclosure";

// Redirect to login on 401
const handle401 = () => {
  sessionStorage.setItem("redirectAfterAuth", window.location.pathname + window.location.search);
  const path = window.location.pathname;
  if (path.startsWith("/extended-warranty")) window.location.href = "/extended-warranty/welcome";
  else if (path.startsWith("/renewal")) window.location.href = "/renewal/welcome";
  else window.location.href = "/onboarding/welcome";
};

const ReinQuickConnectScreen = ({ buyerData, onAccept }) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [showBuyerModal, setShowBuyerModal] = useState(false);
  const [whyMatchOpen, setWhyMatchOpen] = useState(false);
  const { getIdToken } = useAuth();
  const { trackButtonClick } = useInteractionTracking();
  const { flowType } = useFlow();
  const headerRef = useRef(null);

  // Fire confetti on mount
  useEffect(() => {
    if (!headerRef.current) return;
    const rect = headerRef.current.getBoundingClientRect();
    const x = (rect.left + rect.width / 2) / window.innerWidth;
    const y = (rect.top + rect.height / 2) / window.innerHeight;
    confetti({
      particleCount: 100,
      spread: 80,
      origin: { x, y },
      colors: ["#7B61FF", "#D4F6C9", "#CEC5FF", "#FFD700", "#A1CE90"],
    });
  }, []);

  useEffect(() => {
    analyticsService.trackPageView("rein_quick_connect", {
      buyer: buyerData?.name,
    });
  }, []);

  const handleStartSaving = async () => {
    setIsProcessing(true);
    setError(null);

    // Parse URL params for tracking context
    const urlParams = new URLSearchParams(window.location.search);
    const partner = urlParams.get("partner") || urlParams.get("p");
    const utmParams = {
      utm_source: urlParams.get("utm_source"),
      utm_medium: urlParams.get("utm_medium"),
      utm_campaign: urlParams.get("utm_campaign"),
      utm_content: urlParams.get("utm_content"),
      utm_term: urlParams.get("utm_term"),
    };

    trackButtonClick("start-saving-now", {
      step_name: "rein_quick_connect",
      buyer: buyerData.name,
    });

    try {
      const token = await getIdToken();
      if (!token) throw new Error("Authentication required");

      const response = await fetch(`${API_CONFIG.BASE_URL}/api/users/insurance-profile/accept/quick-connect`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-user-token": token,
        },
        body: JSON.stringify({
          dataBuyerId: buyerData.dataBuyerId,
        }),
      });

      if (response.status === 401) { handle401(); return; }

      // Navigate to Rein call screen (backend handles Rein create-offer)
      onAccept(buyerData);

      analyticsService.trackEvent("quick_connect_conversion", {
        buyer_name: buyerData.name,
        buyer_id: buyerData.dataBuyerId,
        offer_amount: buyerData.offer?.amount,
        partner,
        ...utmParams,
        flow_type: flowType,
        api_success: true,
        category: "Conversion",
      });
    } catch (err) {
      console.error("Error in quick-connect:", err);
      setError(err.message);
      // Still navigate on error — non-blocking
      onAccept(buyerData);

      analyticsService.trackEvent("quick_connect_conversion", {
        buyer_name: buyerData.name,
        buyer_id: buyerData.dataBuyerId,
        offer_amount: buyerData.offer?.amount,
        partner,
        ...utmParams,
        flow_type: flowType,
        api_success: false,
        error_message: err.message,
        category: "Conversion",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  if (!buyerData) return null;

  return (
    <div className="space-y-6">
      {error && (
        <div className="flex items-start gap-2 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
          <p>{error}</p>
        </div>
      )}

      {/* Success Header */}
      <div className="text-center space-y-3" ref={headerRef}>
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
          <Check className="w-8 h-8 text-green-600" />
        </div>
        <h1 className="text-2xl font-bold font-bebas uppercase">
          Congrats!
        </h1>
        <p className="text-gray-600">
          We found a match
        </p>
      </div>

      {/* Buyer Match Card — without "They paid to reach you" */}
      <div className="border border-gray-200 rounded-2xl overflow-hidden">
        <div className="flex items-center gap-4 p-4">
          <div className="w-14 h-14 bg-violet-100 rounded-xl flex items-center justify-center p-2 flex-shrink-0">
            <img
              className="w-full h-full object-contain"
              src={buyerData.logo}
              alt={buyerData.name}
            />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-base font-bold text-darkPurple">
              {buyerData.name}
            </h2>
            <p className="text-sm text-gray-500">
              Matched to your profile · Verified by Ownli
            </p>
          </div>
          <a
            onClick={() => {
              trackButtonClick("buyer-detail-modal", { step_name: "rein_quick_connect" });
              setShowBuyerModal(true);
            }}
            className="text-[#7B61FF] font-medium cursor-pointer text-sm flex-shrink-0"
          >
            Learn more
          </a>
        </div>

        <div className="border-t border-gray-200" />

        <div className="p-4">
          <button
            onClick={() => setWhyMatchOpen((prev) => !prev)}
            className="flex items-center justify-between w-full text-left"
          >
            <p className="text-xs uppercase tracking-wide font-bold text-gray-500">
              Why {buyerData.name} matched with you
            </p>
            <ChevronDown
              className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${
                whyMatchOpen ? "rotate-180" : ""
              }`}
            />
          </button>

          <div
            className={`overflow-hidden transition-all duration-200 ${
              whyMatchOpen ? "max-h-96 opacity-100 mt-4" : "max-h-0 opacity-0"
            }`}
          >
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <span className="text-lg flex-shrink-0">🎯</span>
                <p className="text-sm">
                  <span className="font-bold">Your profile fits their specialty.</span>{" "}
                  {buyerData.name} works with carriers best suited for drivers with
                  your coverage history and location.
                </p>
              </div>
              <div className="flex items-start gap-3">
                <span className="text-lg flex-shrink-0">💰</span>
                <p className="text-sm">
                  <span className="font-bold">They compete, not just quote.</span>{" "}
                  {buyerData.name} works with some of the best carriers to find the most competitive
                  rate. They're incentivized to win your business.
                </p>
              </div>
              {/* "They paid to reach you" reason removed for quick-connect */}
            </div>
          </div>
        </div>
      </div>

      <BuyerInfoModal
        open={showBuyerModal}
        onOpenChange={setShowBuyerModal}
        buyer={buyerData}
      />

      {/* Action box — no dollar amounts, no h2 */}
      <div className="border-2 border-[#7B61FF] rounded-2xl p-5 space-y-3 bg-[#F5F2FF]">
        <button
          onClick={handleStartSaving}
          disabled={isProcessing}
          className="w-full flex items-center justify-center gap-2 bg-[#7B61FF] text-white px-4 py-4 rounded-xl hover:bg-[#6A50EE] transition-colors font-bold text-lg shadow-lg shadow-[#7B61FF]/30 uppercase"
        >
          {isProcessing ? (
            <>
              <Loader className="w-4 h-4 animate-spin" />
              <span>Processing...</span>
            </>
          ) : (
            "Start Saving Now"
          )}
        </button>
        <ReinTcpaDisclosure buttonLabel="Start Saving Now" />
      </div>

      {/* What Happens Next — amounts hidden */}
      <div className="border border-gray-200 rounded-2xl p-5 space-y-4">
        <p className="text-xs uppercase tracking-wide font-bold text-gray-500">
          What Happens Next
        </p>
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <div className="w-7 h-7 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-green-700 text-sm font-bold">1</span>
            </div>
            <p className="text-sm">
              <span className="font-bold">{buyerData.name} gets to work</span>{" "}
              — they'll build a real, bindable quote from your verified
              profile.
            </p>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-7 h-7 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-green-700 text-sm font-bold">2</span>
            </div>
            <p className="text-sm">
              <span className="font-bold">Call them now or wait</span> —{" "}
              {buyerData.name} will follow up within one business day if you
              prefer.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReinQuickConnectScreen;
